import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Input from '../../components/Input.js';
import Password from '../../components/Password.js';
import 'whatwg-fetch';
import { companyLogin, verifyOtp, resendOtp } from '../../api/authentication.js';
import { useSecurity } from '../../components/Security/security.js';
import { Link, Container, Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { isValidEmail } from '../../utils/validation.js';
import { StoreContext } from '../../components/Store';
import { LANDINGPAGE_URL } from '../../utils/msal-config.js';
import Alert from '@mui/material/Alert';

const SCREENS = { LOGIN: 'LOGIN', MFA: 'MFA', IDP_MESSAGE: 'IDP_MESSAGE' };

const IDPMessage = () => {
  return (
    <Box sx={{ p: 4 }}>
      Your account has been set up to use Single Sign-On (SSO) for authentication.
      <br />
      <br />
      Please click below to proceed to the SSO login page.
      <br />
      <br />
      <a href={LANDINGPAGE_URL} className="authentication-button">
        Login via SSO
      </a>
    </Box>
  );
};

const MFAScreen = ({ handleMFASubmit, error, values, setValues, isSubmitting }) => {
  const [disableResend, setDisableResend] = useState(false);
  const [resendTimeout, setResendTimeout] = useState(null);

  useEffect(() => {
    return () => clearTimeout(resendTimeout);
  }, [resendTimeout]);

  const onResendClick = async () => {
    setDisableResend(true);

    await resendOtp(values.otpToken);

    const timeout = setTimeout(() => {
      setDisableResend(false);
    }, 30000); // Disable resend for 30 seconds
    setResendTimeout(timeout);
  };

  return (
    <Box sx={{ p: 4 }}>
      <form onSubmit={handleMFASubmit}>
        <Box sx={{ mb: 2 }}>
          We have sent a 6 digit access code to your email. It may take a few minutes to arrive. Please enter it below
          to continue.
        </Box>

        {error && (
          <Grid sx={{ mb: 2 }}>
            <Alert severity="error">
              {error}
              {error.includes('locked') && (
                <>
                  {'. '}
                  Please contact <a href="mailto:ICSSupport@eori.uk">IT support</a>.
                </>
              )}
            </Alert>
          </Grid>
        )}

        <Box>Enter access code</Box>
        <Grid container justifyContent="space-between" alignItems="start">
          <Grid>
            <Input
              name="otp"
              type="text"
              label="Access code"
              value={values.otp}
              onChange={(e) => setValues((prev) => ({ ...prev, otp: e.target.value }))}
              hideLabel
            />
          </Grid>
          <Grid>
            <button
              style={{ height: '41px', width: '8rem', margin: '0.25rem 0' }}
              className="authentication-button"
              disabled={isSubmitting}
            >
              Verify
            </button>
          </Grid>
        </Grid>

        {disableResend ? (
          'Access code resent - please check your inbox.'
        ) : (
          <Link variant="body2" onClick={onResendClick} style={{ cursor: 'pointer' }}>
            Resend Access Code
          </Link>
        )}
      </form>
    </Box>
  );
};

const LoginScreen = ({ handleLoginSubmit, error, values, setValues, isSubmitting }) => {
  return (
    <form onSubmit={handleLoginSubmit} id="authentication-form">
      <Input
        name="username"
        type="text"
        label="Username"
        value={values.username}
        onChange={(e) => setValues((prev) => ({ ...prev, username: e.target.value }))}
      />

      <Password
        name="password"
        label="Password"
        value={values.password}
        onChange={(e) => setValues((prev) => ({ ...prev, password: e.target.value }))}
      />

      <span>{error}&nbsp;</span>
      <button
        style={{ height: '2.5rem', marginTop: '0.1rem', maxWidth: '8rem' }}
        className="authentication-button"
        disabled={isSubmitting}
      >
        Login
      </button>
      <div style={{ marginBottom: '1rem' }}>
        <Link href="/PasswordReset" variant="body2">
          Reset Password
        </Link>
      </div>
    </form>
  );
};

const Login = () => {
  const store = useContext(StoreContext);
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('sessionExpired') || 'false';
  const [values, setValues] = useState({ username: '', password: '', otp: '', otpToken: '' });

  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { dispatch } = useSecurity();
  const [currentScreen, setCurrentScreen] = useState(SCREENS.LOGIN);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Session storage is not updated immediately so need to ensure is purged on mounting
    console.log(error);
    sessionStorage.removeItem('reg');
    sessionStorage.removeItem('type');
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('company');
    sessionStorage.removeItem('companyid');
    sessionStorage.removeItem('name');
    sessionStorage.removeItem('role');
    sessionStorage.removeItem('trader');
    sessionStorage.removeItem('lsp');
    sessionStorage.removeItem('wlp');
    sessionStorage.removeItem('admin');
    sessionStorage.removeItem('fullName');
    sessionStorage.removeItem('department');
    sessionStorage.removeItem('lastLoggedIn');
    sessionStorage.removeItem('sso');
    store.companyId[1](null);
  }, []);

  const handleLogin = (data) => {
    sessionStorage.setItem('token', data.token);
    sessionStorage.setItem('company', data.company);
    sessionStorage.setItem('companyid', data.companyid);
    sessionStorage.setItem('name', data.name);
    sessionStorage.setItem('reg', data.reg);
    sessionStorage.setItem('type', data.type);
    sessionStorage.setItem('fullName', data.fullName);
    sessionStorage.setItem('role', data.role);
    sessionStorage.setItem('department', data.department);
    sessionStorage.setItem('admin', data.admin);
    sessionStorage.setItem('trader', data.trader);
    sessionStorage.setItem('lsp', data.lsp);
    sessionStorage.setItem('lsp', data.wlp);
    sessionStorage.setItem('lastLoggedIn', data.lastLoggedIn);
    sessionStorage.setItem('sso', 0);
    store.companyId[1](data.companyid);

    dispatch({ type: 'login', detail: data });
    navigate('/dashboard');
  };

  const handleLoginSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      setError('');

      if (values.username?.trim() === '' || values.password?.trim() === '') {
        setError('Please enter your username and password.');
        return;
      }

      if (!isValidEmail(values.username)) {
        setError('Please enter a valid email address.');
        return;
      }

      const data = await companyLogin(event.target);

      if (data.expired === true) {
        setError('Your password has expired.');
      } else if (data.useIdpLogin) {
        setError('');
        setCurrentScreen(SCREENS.IDP_MESSAGE);
      } else if (data.otpVerified === false) {
        setCurrentScreen(SCREENS.MFA);
        setValues((prev) => ({ ...prev, otpToken: data.token }));
        return;
      } else if (data.success) {
        handleLogin(data);
      } else {
        setError(data.err);
      }
    } catch (err) {
      console.error(err);
      setError('Server error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleMFASubmit = async (event) => {
    event.preventDefault();

    if (values.otp?.trim() === '') {
      setError('Please enter your access code.');
      return;
    }

    setIsSubmitting(true);

    try {
      setError('');
      const data = await verifyOtp(event.target, values.otpToken);

      if (data.success && data.token) {
        handleLogin(data);
      } else {
        setError(data.err || 'Invalid access code. Please try again.');
      }
    } catch (err) {
      console.error(err);
      setError('Server error.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="md">
      <Box
        sx={{
          p: 4,
          mt: 2,
          mb: 6,
        }}
      ></Box>

      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2} className="authentication-container">
          {sessionExpired === 'true' && (
            <Grid size={8}>
              <Alert severity="error">Your session has expired. Please log in again.</Alert>
            </Grid>
          )}

          <Grid size={8} className="authentication-container-inner">
            <div className="authentication-header">CABIE ICS2 Portal</div>
            {currentScreen === SCREENS.IDP_MESSAGE && <IDPMessage />}
            {currentScreen === SCREENS.LOGIN && (
              <LoginScreen
                handleLoginSubmit={handleLoginSubmit}
                error={error}
                values={values}
                setValues={setValues}
                isSubmitting={isSubmitting}
              />
            )}
            {currentScreen === SCREENS.MFA && (
              <MFAScreen
                handleMFASubmit={handleMFASubmit}
                error={error}
                values={values}
                setValues={setValues}
                isSubmitting={isSubmitting}
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default Login;
